import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Robot, X, PaperPlaneTilt, Spinner, ArrowClockwise, CheckCircle, Sparkle, Warning, FolderOpen, Microphone, SpeakerHigh, Pause, Play, Globe, Folder } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import MarkdownMessage from "../common/MarkdownMessage";
import { toast } from "sonner";
import { sendAssistantMessage, getAssistantHistory, getSubAgents, runSubAgent, transcribeVoice } from "../../api";
import { getProject } from "../../api/projects";
import { useTextToSpeech } from "../../hooks/useTextToSpeech";
import { useNotifications } from "../../contexts/NotificationsContext";

const canRecordVoice = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

// No per-tenant language setting exists yet — default to the app's existing
// locale convention (en-AU is already used for date/number formatting throughout).
const ASSISTANT_TTS_LANGUAGE = "en-AU";

const STORAGE_KEY = "drawfire_assistant_session_id";
const PROJECT_ROUTE_RE = /^\/projects\/([^/]+)$/;

function makeSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getOrCreateSessionId() {
  try {
    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
  } catch (_) { /* localStorage unavailable — fall through */ }
  const fresh = makeSessionId();
  try { window.localStorage.setItem(STORAGE_KEY, fresh); } catch (_) { /* ignore */ }
  return fresh;
}

const STARTER_ACTIONS = [
  { label: "Bring me up to speed", text: "Bring me up to speed on this project." },
  { label: "Tell me the next steps", text: "What are the next steps for this project?" },
];

/**
 * Derive a page_type identifier from the current URL pathname.
 * Used to send context to the backend so the AI knows where the user is.
 */
function derivePageType(pathname) {
  if (PROJECT_ROUTE_RE.test(pathname)) return "project_detail";
  if (pathname.startsWith("/explore-files")) return "explore_files";
  if (pathname.startsWith("/open-analysis")) return "drawing_analysis";
  if (pathname.startsWith("/evaluate")) return "drawing_evaluation";
  if (pathname.startsWith("/compare")) return "drawing_comparison";
  if (pathname.startsWith("/ai-automations")) return "ai_automations";
  if (pathname.startsWith("/settings")) return "settings";
  if (pathname.startsWith("/recordings")) return "recordings";
  if (pathname.startsWith("/projects")) return "projects_list";
  if (pathname === "/") return "dashboard";
  return "unknown";
}

/**
 * Rough markdown → plain-text cleanup so the browser's speech synthesis
 * doesn't read out literal symbols (asterisks, link brackets, etc).
 */
function stripMarkdownForSpeech(text) {
  return (text || "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_`#>~]+/g, "")
    .replace(/\n{2,}/g, ". ")
    .replace(/\s+/g, " ")
    .trim();
}

 /* Derive a human-friendly scope label and color scheme from the current page context.
 */
function deriveScopeInfo(pageType, projectName, folderPath) {
  if (pageType === "project_detail" && projectName) {
    return { label: `Project: ${projectName}`, color: "bg-blue-50 text-blue-700 border-blue-200", icon: "folder" };
  }
  if (pageType === "explore_files") {
    const path = folderPath || "OneDrive";
    return { label: `Files: ${path}`, color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: "files" };
  }
  return { label: "Global Assistant", color: "bg-slate-50 text-slate-600 border-slate-200", icon: "globe" };
}

/**
 * AssistantChatWidget — a floating, system-wide AI assistant chat bubble.
 * Talks to POST /api/assistant/chat (Gemini + function calling on the backend).
 * When the user is viewing a specific project's detail page (/projects/:id),
 * it auto-switches into a Project Assistant context — a separate chat thread
 * scoped strictly to that project's own data, with quick starter actions.
 * Everywhere else it's the general Master Agent (can create projects, answer
 * platform-wide questions, and hand off to the Project Assistant itself).
 */
export default function AssistantChatWidget() {
  const location = useLocation();
  const { refresh: refreshNotifications } = useNotifications();
  const activeProjectId = useMemo(() => {
    const match = PROJECT_ROUTE_RE.exec(location.pathname);
    return match ? match[1] : null;
  }, [location.pathname]);

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [projectName, setProjectName] = useState(null);
  const [messages, setMessages] = useState([]); // [{id, role, content, actions?}]
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [subAgents, setSubAgents] = useState([]);
  const hasActiveSubAgents = subAgents.some((a) => ["queued", "running"].includes(a.status));
  const lastContextKeyRef = useRef(null); // the effectiveSessionId we last hydrated/started fresh for
  const currentSessionRef = useRef(null);
  const deliveredSubAgentIdsRef = useRef(new Set());
  const startedSubAgentIdsRef = useRef(new Set());
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const idCounter = useRef(0);
  const nextId = () => `m-${Date.now()}-${idCounter.current++}`;

  // ── Voice input (push-to-talk mic → Gemini transcription) ──────────────
  const [recordingState, setRecordingState] = useState("idle"); // idle | recording | transcribing
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // ── Voice output (read a reply aloud via browser speech synthesis) ──────
  const tts = useTextToSpeech(ASSISTANT_TTS_LANGUAGE);

  const effectiveSessionId = useMemo(
    () => (sessionId && activeProjectId ? `${sessionId}::project::${activeProjectId}` : sessionId),
    [sessionId, activeProjectId]
  );

  useEffect(() => {
    setSessionId(getOrCreateSessionId());
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  // Fetch the project's display name for the panel header whenever we enter a project context.
  useEffect(() => {
    if (!activeProjectId) { setProjectName(null); return; }
    getProject(activeProjectId)
      .then((project) => setProjectName(project?.name || null))
      .catch(() => setProjectName(null));
  }, [activeProjectId]);

  // (Re)hydrate history whenever the panel is open AND the effective chat
  // context (master vs. this specific project) actually changed — so
  // switching between a project page and elsewhere swaps threads cleanly.
  useEffect(() => {
    if (!open || !effectiveSessionId || lastContextKeyRef.current === effectiveSessionId) return;
    lastContextKeyRef.current = effectiveSessionId;
    currentSessionRef.current = effectiveSessionId;
    deliveredSubAgentIdsRef.current = new Set();
    startedSubAgentIdsRef.current = new Set();
    setMessages([]);
    setLoadingHistory(true);
    getAssistantHistory(effectiveSessionId)
      .then((res) => {
        const hist = (res.data?.messages || []).map((m) => ({
          id: nextId(), role: m.role, content: m.content, subAgentId: m.sub_agent_id,
        }));
        hist.forEach((m) => {
          if (m.subAgentId) deliveredSubAgentIdsRef.current.add(m.subAgentId);
        });
        if (hist.length > 0) setMessages(hist);
      })
      .catch(() => { /* non-fatal — start with an empty conversation */ })
      .finally(() => setLoadingHistory(false));
  }, [open, effectiveSessionId]);

  const startNewChat = useCallback(() => {
    const fresh = makeSessionId();
    try { window.localStorage.setItem(STORAGE_KEY, fresh); } catch (_) { /* ignore */ }
    const freshEffective = activeProjectId ? `${fresh}::project::${activeProjectId}` : fresh;
    lastContextKeyRef.current = freshEffective; // it's brand-new — nothing to hydrate
    currentSessionRef.current = freshEffective;
    deliveredSubAgentIdsRef.current = new Set();
    startedSubAgentIdsRef.current = new Set();
    setSessionId(fresh);
    setMessages([]);
    setSubAgents([]);
  }, [activeProjectId]);

  const showCompletedSubAgent = useCallback((agent, targetSessionId = effectiveSessionId) => {
    if (
      !agent?.id
      || !["completed", "failed"].includes(agent.status)
      || currentSessionRef.current !== targetSessionId
      || deliveredSubAgentIdsRef.current.has(agent.id)
    ) return;
    deliveredSubAgentIdsRef.current.add(agent.id);
    setMessages((prev) => [
      ...prev,
      {
        id: nextId(),
        role: "assistant",
        content: agent.status === "completed"
          ? `**Background task complete**\n\n${agent.result_summary || "Task completed."}`
          : `**Background task couldn't finish**\n\n${agent.result_summary || "Please try the task again."}`,
        subAgentId: agent.id,
        isError: agent.status === "failed",
      },
    ]);
  }, [effectiveSessionId]);

  const launchSubAgent = useCallback((action, targetSessionId = effectiveSessionId) => {
    if (!action?.sub_agent_id || startedSubAgentIdsRef.current.has(action.sub_agent_id)) return;
    startedSubAgentIdsRef.current.add(action.sub_agent_id);
    const queued = {
      id: action.sub_agent_id,
      agent_type: action.agent_type,
      task_description: action.task_description,
      status: "queued",
    };
    setSubAgents((prev) => [queued, ...prev.filter((a) => a.id !== queued.id)]);
    runSubAgent(action.sub_agent_id)
      .then((res) => {
        const completed = res.data;
        setSubAgents((prev) => prev.map((a) => a.id === completed.id ? { ...a, ...completed } : a));
        showCompletedSubAgent(completed, targetSessionId);
      })
      .catch(() => {
        startedSubAgentIdsRef.current.delete(action.sub_agent_id);
      });
  }, [effectiveSessionId, showCompletedSubAgent]);

  useEffect(() => {
    if (!open || !effectiveSessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await getSubAgents(effectiveSessionId);
        if (cancelled) return;
        const agents = res.data?.sub_agents || [];
        setSubAgents(agents);
        agents.forEach((agent) => showCompletedSubAgent(agent, effectiveSessionId));
        agents
          .filter((agent) => agent.status === "queued")
          .forEach((agent) => launchSubAgent({
            sub_agent_id: agent.id,
            agent_type: agent.agent_type,
            task_description: agent.task_description,
          }, effectiveSessionId));
      } catch (_) { /* non-fatal */ }
    };
    poll();
    const interval = hasActiveSubAgents ? setInterval(poll, 2500) : null;
    return () => { cancelled = true; clearInterval(interval); };
  }, [open, effectiveSessionId, hasActiveSubAgents, showCompletedSubAgent, launchSubAgent]);

  const sendMessage = useCallback(async (text) => {
    if (!text || sending || !effectiveSessionId) return;
    setInput("");
    setMessages((prev) => [...prev, { id: nextId(), role: "user", content: text }]);
    setSending(true);
    try {
      // Build page context from current route
      const pageContext = {
        route: location.pathname,
        page_type: derivePageType(location.pathname),
        project_id: activeProjectId || undefined,
        project_name: projectName || undefined,
      };
      const res = await sendAssistantMessage(effectiveSessionId, text, activeProjectId, pageContext);
      const { reply, actions } = res.data;
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: "assistant", content: reply, actions: actions || [] },
      ]);
      (actions || [])
        .filter((a) => a.type === "sub_agent_spawned")
        .forEach((a) => launchSubAgent(a, effectiveSessionId));
      (actions || []).forEach((a) => {
        if (a.type === "project_created") {
          toast.success(a.message || `Project "${a.name}" created`);
        } else if (a.type === "project_status_updated") {
          toast.success(a.message || `"${a.name}" status updated to ${a.new_status_label}`);
        } else if (a.type === "project_details_updated") {
          toast.success(a.message || `"${a.name}" details updated`);
        } else if (a.type === "analysis_complete") {
          toast.success(a.message || "Drawing analysis complete");
        } else if (a.type === "comparison_complete") {
          toast.success(a.message || "Drawing comparison complete");
        } else if (a.type?.startsWith("notification_")) {
          refreshNotifications();
          toast.success(a.message || "Notification updated");
        }
      });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const friendly = e.response?.status === 503
        ? "The assistant is temporarily unavailable — please try again shortly."
        : (detail || "Something went wrong — please try again.");
      setMessages((prev) => [...prev, { id: nextId(), role: "assistant", content: friendly, isError: true }]);
    } finally {
      setSending(false);
    }
  }, [sending, effectiveSessionId, activeProjectId, location.pathname, projectName, refreshNotifications, launchSubAgent]);

  const runningSubAgents = subAgents.filter((a) => ["queued", "running"].includes(a.status));

  // Stop mic tracks if the widget unmounts mid-recording (speech-synthesis cleanup lives inside the hook).
  useEffect(() => () => {
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const handleReadAloudClick = useCallback((msg) => {
    const isThisMessage = tts.currentMessageId === msg.id;
    if (isThisMessage && tts.isPlaying) {
      tts.pause();
    } else if (isThisMessage && tts.isPaused) {
      tts.resume();
    } else {
      // Idle, or a DIFFERENT message is currently playing/paused — stop it and read this one.
      if (tts.currentMessageId && tts.currentMessageId !== msg.id) tts.stopAll();
      tts.speak(stripMarkdownForSpeech(msg.content), msg.id);
    }
  }, [tts]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      audioChunksRef.current = [];
      const preferredType = ["audio/webm", "audio/mp4", "audio/wav", "audio/ogg"]
        .find((t) => MediaRecorder.isTypeSupported(t)) || "";
      const recorder = new MediaRecorder(stream, preferredType ? { mimeType: preferredType } : undefined);
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
        // Use the recorder's ACTUAL mime type (browser-dependent — e.g. Safari records
        // audio/mp4, not webm) so the blob/filename sent to the backend match its real codec.
        const actualType = recorder.mimeType || preferredType || "audio/webm";
        const blob = new Blob(audioChunksRef.current, { type: actualType });
        if (blob.size === 0) { setRecordingState("idle"); return; }
        setRecordingState("transcribing");
        try {
          const res = await transcribeVoice(blob);
          const text = (res.data?.text || "").trim();
          // Voice is just another way to PRODUCE text — drop it into the input box for the
          // user to review/edit, exactly like typing. Never auto-send on their behalf.
          if (text) {
            setInput(text);
            requestAnimationFrame(() => chatInputRef.current?.focus());
          } else {
            toast.error("Didn't catch that — please try again.");
          }
        } catch (e) {
          const detail = e.response?.data?.detail;
          toast.error(detail ? `Voice transcription failed: ${detail}` : "Voice transcription failed — please try again.");
        } finally {
          setRecordingState("idle");
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecordingState("recording");
    } catch (e) {
      toast.error("Microphone access was denied or is unavailable.");
    }
  }, []);

  const stopRecording = useCallback(() => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      } else {
        setRecordingState("idle");
      }
    } catch (e) {
      setRecordingState("idle");
    }
  }, []);

  const handleMicClick = useCallback(() => {
    if (recordingState === "recording") stopRecording();
    else if (recordingState === "idle") startRecording();
  }, [recordingState, startRecording, stopRecording]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    sendMessage(text);
  }, [input, sendMessage]);

  return (
    <>
      {/* Floating bubble trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assistant-bubble-button"
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-primary hover:bg-primary-hover
                     text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center
                     hover:scale-105"
          aria-label="Open AI Assistant"
        >
          <Sparkle size={24} weight="fill" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-40 w-[480px] h-[680px] max-h-[85vh] bg-white rounded-2xl
                     shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          data-testid="assistant-chat-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-primary text-white flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              {activeProjectId ? <FolderOpen size={18} className="flex-shrink-0" /> : <Robot size={18} className="flex-shrink-0" />}
              <div className="min-w-0" data-testid="assistant-context-label">
                <span className="text-sm font-semibold block truncate">
                  {activeProjectId ? "Project Assistant" : "DrawFire Assistant"}
                </span>
                {activeProjectId && (
                  <span className="text-[11px] text-white/80 block truncate">{projectName || "Loading project…"}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={startNewChat}
                title="Start a new conversation"
                data-testid="assistant-new-chat-button"
                className="p-1.5 rounded hover:bg-white/15 transition-colors"
              >
                <ArrowClockwise size={16} />
              </button>
              <button
                onClick={() => setOpen(false)}
                title="Close"
                data-testid="assistant-close-button"
                className="p-1.5 rounded hover:bg-white/15 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {runningSubAgents.length > 0 && (
            <div
              className="flex items-center gap-2 border-b border-blue-200 bg-blue-50 px-3 py-2 text-[11px] font-medium text-blue-700"
              data-testid="assistant-background-status"
            >
              <Spinner size={12} className="animate-spin" />
              {runningSubAgents.length} background task{runningSubAgents.length > 1 ? "s" : ""} working
              <span className="ml-auto font-normal text-blue-600">You can keep chatting</span>
            </div>
          )}

          {/* Scope indicator */}
          {(() => {
            const pageType = derivePageType(location.pathname);
            const scope = deriveScopeInfo(pageType, projectName, null);
            return (
              <div
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium border-b ${scope.color} flex-shrink-0`}
                data-testid="assistant-scope-badge"
              >
                {scope.icon === "folder" && <FolderOpen size={12} />}
                {scope.icon === "files" && <Folder size={12} />}
                {scope.icon === "globe" && <Globe size={12} />}
                <span className="truncate">{scope.label}</span>
              </div>
            );
          })()}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-slate-50">
            {loadingHistory && (
              <div className="flex items-center justify-center gap-2 text-slate-400 text-xs py-4">
                <Spinner size={14} className="animate-spin" /> Loading conversation...
              </div>
            )}

            {!loadingHistory && messages.length === 0 && (
              <div className="text-center text-slate-500 text-sm px-4 py-8" data-testid="assistant-empty-state">
                {activeProjectId ? (
                  <>
                    <FolderOpen size={28} className="mx-auto mb-2 text-primary/60" />
                    <p className="font-medium text-slate-600">
                      I&apos;m scoped to {projectName ? `"${projectName}"` : "this project"} right now.
                    </p>
                    <p className="text-xs mt-1 text-slate-400">
                      Ask about its files, status, financials, or communications.
                    </p>
                    <div className="flex flex-col gap-2 mt-4">
                      {STARTER_ACTIONS.map((sa) => (
                        <button
                          key={sa.label}
                          onClick={() => sendMessage(sa.text)}
                          disabled={sending}
                          data-testid={`assistant-starter-action-${sa.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="text-xs font-medium px-3 py-2 rounded-lg border border-primary/30 text-primary
                                     bg-primary/5 hover:bg-primary/10 transition-colors disabled:opacity-50"
                        >
                          {sa.label}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <Robot size={28} className="mx-auto mb-2 text-primary/60" />
                    <p className="font-medium text-slate-600">Hi, I am your DrawFire Assistant.</p>
                    <p className="text-xs mt-1 text-slate-400">
                      Ask me to create a new project, or ask about your active projects and invoices.
                    </p>
                  </>
                )}
              </div>
            )}

            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[88%] px-3.5 py-2.5 ${
                  msg.role === "user" ? "message-user" : "message-ai"
                } ${msg.isError ? "!bg-red-50 !text-red-700" : ""}`}>
                  {msg.role === "assistant" ? (
                    <>
                      {msg.isError && (
                        <div className="flex items-center gap-1.5 mb-1">
                          <Warning size={13} />
                          <span className="text-xs font-medium">Assistant error</span>
                        </div>
                      )}
                      <MarkdownMessage content={msg.content} />
                      {(msg.actions || []).map((a, i) => (
                        <div key={i}>
                          {a.type === "project_created" && (
                            <div className="mt-2 flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                              <CheckCircle size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" weight="fill" />
                              <div className="text-xs text-emerald-800">
                                <p className="font-semibold">Project created: {a.name}</p>
                                {a.folder_warning && (
                                  <p className="text-amber-700 mt-1">{a.folder_warning}</p>
                                )}
                              </div>
                            </div>
                          )}
                          {a.type === "project_status_updated" && (
                            <div className="mt-2 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                              <CheckCircle size={16} className="text-blue-600 flex-shrink-0 mt-0.5" weight="fill" />
                              <div className="text-xs text-blue-800">
                                <p className="font-semibold">
                                  {a.name}: status changed to {a.new_status_label}
                                </p>
                              </div>
                            </div>
                          )}
                          {a.type === "project_details_updated" && (
                            <div className="mt-2 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                              <CheckCircle size={16} className="text-blue-600 flex-shrink-0 mt-0.5" weight="fill" />
                              <div className="text-xs text-blue-800">
                                <p className="font-semibold">{a.name}: details updated</p>
                                {a.updated_fields && (
                                  <p className="mt-0.5 text-blue-700">
                                    {Object.entries(a.updated_fields)
                                      .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v || "—"}`)
                                      .join(" · ")}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                          {a.type?.startsWith("notification_") && (
                            <div
                              className="mt-2 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2"
                              data-testid={`assistant-notification-action-${msg.id}-${i}`}
                            >
                              <CheckCircle size={16} className="mt-0.5 flex-shrink-0 text-emerald-600" weight="fill" />
                              <p className="text-xs font-semibold text-emerald-800">
                                {a.message || "Notification updated"}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                      {tts.isSupported && (() => {
                        const isThisMessage = tts.currentMessageId === msg.id;
                        const isPlayingThis = isThisMessage && tts.isPlaying;
                        const isPausedThis = isThisMessage && tts.isPaused;
                        return (
                          <button
                            onClick={() => handleReadAloudClick(msg)}
                            title={isPlayingThis ? "Pause reading" : isPausedThis ? "Resume reading" : "Read this reply aloud"}
                            data-testid={`assistant-message-speak-button-${msg.id}`}
                            className={`mt-2 flex items-center gap-1 text-[11px] transition-colors ${
                              isPlayingThis || isPausedThis ? "text-primary" : "text-slate-400 hover:text-primary"
                            }`}
                          >
                            {isPlayingThis ? (
                              <Pause size={12} weight="fill" className="animate-pulse" />
                            ) : isPausedThis ? (
                              <Play size={12} weight="fill" />
                            ) : (
                              <SpeakerHigh size={12} />
                            )}
                            <span>{isPlayingThis ? "Pause" : isPausedThis ? "Resume" : "Read aloud"}</span>
                          </button>
                        );
                      })()}
                    </>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  )}
                </div>
              </div>
            ))}

            {sending && (
              <div className="flex justify-start">
                <div className="message-ai px-3.5 py-2.5 flex items-center gap-1.5">
                  <Spinner size={14} className="animate-spin text-primary" />
                  <span className="text-xs text-slate-500">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="flex-shrink-0 border-t border-slate-200 bg-white p-3">
            <div className="flex gap-2">
              <Input
                ref={chatInputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder={
                  recordingState === "recording" ? "Listening..." :
                  recordingState === "transcribing" ? "Transcribing your question..." :
                  activeProjectId ? "Ask about this project..." : "Ask me anything, or say 'create a new project'..."
                }
                disabled={sending || recordingState !== "idle"}
                className="flex-1 rounded-sm text-sm"
                data-testid="assistant-chat-input"
              />
              {canRecordVoice && (
                <Button
                  type="button"
                  onClick={handleMicClick}
                  disabled={sending || recordingState === "transcribing"}
                  size="sm"
                  variant="outline"
                  title={recordingState === "recording" ? "Stop recording" : "Ask by voice"}
                  data-testid="assistant-voice-input-button"
                  className={`rounded-sm flex-shrink-0 ${
                    recordingState === "recording" ? "!bg-red-50 !border-red-300 !text-red-600" : ""
                  }`}
                >
                  {recordingState === "transcribing" ? (
                    <Spinner size={15} className="animate-spin" />
                  ) : (
                    <Microphone size={15} weight={recordingState === "recording" ? "fill" : "regular"}
                                className={recordingState === "recording" ? "animate-pulse" : ""} />
                  )}
                </Button>
              )}
              <Button
                onClick={handleSend}
                disabled={!input.trim() || sending}
                size="sm"
                className="rounded-sm bg-primary hover:bg-primary-hover flex-shrink-0"
                data-testid="assistant-chat-send"
              >
                {sending ? <Spinner size={15} className="animate-spin" /> : <PaperPlaneTilt size={15} />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
